import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  ArrowLeft, Phone, PhoneCall, Voicemail, Loader2, CheckCircle2, Copy,
  AlertTriangle, PhoneForwarded, Search, RefreshCw, Save, Radio, CalendarPlus,
  ExternalLink, Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';

const BOOKING_FIELDS = [
  ['caller_name', 'Caller'], ['callback_number', 'Callback'], ['service_type', 'Service'],
  ['role_name', 'Role'], ['event_type', 'Event'], ['shift_date', 'Date'], ['end_date', 'End date'],
  ['start_time', 'Start'], ['end_time', 'End'], ['venue_name', 'Venue'], ['venue_address', 'Address'],
  ['city', 'City'], ['state', 'State'], ['talent_needed', 'Talent needed'], ['bill_rate', 'Bill rate'],
  ['deliverables', 'Deliverables'], ['special_instructions', 'Notes'],
];

const SERVICE_LABEL = {
  event_staff: 'Event Staff', ai_ambassador: 'AI Ambassador', digital_content: 'Digital Content',
};
const fmtBookingVal = (k, v) => {
  if (Array.isArray(v)) return v.join(', ');
  if (k === 'service_type') return SERVICE_LABEL[v] || v;
  if (k === 'bill_rate') return `$${v}/hr`;
  return String(v);
};

const STATUS_STYLE = {
  in_progress: 'bg-blue-100 text-blue-700',
  completed: 'bg-emerald-100 text-emerald-700',
  voicemail: 'bg-amber-100 text-amber-700',
  transferred: 'bg-purple-100 text-purple-700',
  missed: 'bg-slate-100 text-slate-600',
  failed: 'bg-rose-100 text-rose-700',
};

const INTENT_LABEL = {
  event_staff: 'Event Staff', ai_ambassador: 'AI Ambassador',
  digital_content: 'Digital Content', booking: 'Booking',
  inquiry: 'Inquiry', staffing_urgent: 'Urgent Staffing',
};

const fmtDT = (d) => (d ? new Date(d).toLocaleString('en-US', {
  month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
}) : '—');
const fmtDur = (s) => (s == null ? '—' : `${Math.floor(s / 60)}m ${s % 60}s`);

export default function EvieCalls() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('calls');

  // ── Calls state ──
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: 'all', follow_up: 'all', q: '' });
  const [detail, setDetail] = useState(null);
  const [detailNotes, setDetailNotes] = useState('');

  // ── Settings state ──
  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);

  const loadCalls = useCallback(async (f) => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      const ff = f || filters;
      if (ff.status !== 'all') qs.set('status', ff.status);
      if (ff.follow_up !== 'all') qs.set('follow_up', ff.follow_up);
      if (ff.q) qs.set('q', ff.q);
      const r = await api.get(`/admin/evie-phone/calls?${qs.toString()}`);
      setData(r.data);
    } catch {
      toast.error('Could not load call log');
    } finally { setLoading(false); }
  }, [filters]);

  const [backfilling, setBackfilling] = useState(false);
  const backfillSummaries = async () => {
    setBackfilling(true);
    try {
      const r = await api.post('/admin/evie-phone/calls/backfill-summaries');
      const { generated, remaining } = r.data || {};
      toast.success(`Generated ${generated} summar${generated === 1 ? 'y' : 'ies'}${remaining ? ` · ${remaining} still pending` : ''}`);
      loadCalls();
    } catch {
      toast.error('Could not backfill summaries');
    } finally { setBackfilling(false); }
  };

  const loadSettings = useCallback(async () => {
    try {
      const r = await api.get('/admin/evie-phone/settings');
      setSettings(r.data);
    } catch {
      toast.error('Could not load phone settings');
    }
  }, []);

  useEffect(() => { loadCalls(); loadSettings(); /* eslint-disable-next-line */ }, []);

  const openDetail = async (id) => {
    try {
      const r = await api.get(`/admin/evie-phone/calls/${id}`);
      setDetail(r.data);
      setDetailNotes(r.data.follow_up_notes || '');
    } catch { toast.error('Could not load call'); }
  };

  const markFollowUp = async (status) => {
    try {
      const r = await api.post(`/admin/evie-phone/calls/${detail.id}/follow-up`, {
        follow_up_status: status, follow_up_notes: detailNotes,
      });
      setDetail(r.data);
      toast.success(status === 'done' ? 'Marked resolved' : 'Reopened');
      loadCalls();
    } catch { toast.error('Could not update'); }
  };

  const saveSettings = async (patch) => {
    setSavingSettings(true);
    try {
      const r = await api.put('/admin/evie-phone/settings', patch);
      setSettings((s) => ({ ...s, ...r.data }));
      toast.success('Settings saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save settings');
    } finally { setSavingSettings(false); }
  };

  const copy = (t) => { navigator.clipboard?.writeText(t); toast.success('Copied'); };

  const createShiftFromCall = () => {
    const b = detail.booking_request || {};
    const notes = [
      b.caller_name ? `Caller: ${b.caller_name}` : '',
      b.callback_number ? `Callback: ${b.callback_number}` : '',
      b.role_name ? `Requested role: ${b.role_name}` : '',
    ].filter(Boolean).join(' · ');
    const prefill = {
      from_call_id: detail.id,
      client_id: detail.client_id || undefined,
      event_title: b.event_type || '',
      venue_name: b.venue_name || '',
      venue_address: b.venue_address || '',
      city: b.city || '',
      state: b.state || '',
      shift_date: b.shift_date || '',
      end_date: b.end_date || '',
      start_time: b.start_time || '',
      end_time: b.end_time || '',
      talent_needed: b.talent_needed || 1,
      bill_rate: b.bill_rate || '',
      is_multi_day: !!(b.end_date && b.end_date !== b.shift_date),
      important_notes: b.special_instructions || '',
      duties: b.deliverables || '',
      additional_notes: notes,
    };
    navigate('/admin/shifts/create', { state: { prefill } });
  };

  const summary = data?.summary || {};
  const calls = data?.calls || [];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="evie-calls-page">
      {/* Header */}
      <div className="bg-slate-900 text-white">
        <div className="max-w-6xl mx-auto px-5 py-6">
          <button onClick={() => navigate('/admin/home')}
            className="flex items-center gap-2 text-slate-300 hover:text-white text-sm mb-4"
            data-testid="evie-calls-back">
            <ArrowLeft className="w-4 h-4" /> Admin Home
          </button>
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-teal-500/15 border border-teal-500/30">
              <PhoneCall className="w-6 h-6 text-teal-300" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold tracking-tight">Evie Telephone Receptionist</h1>
              <p className="text-slate-400 text-sm">Live call log, booking requests, voicemails & setup</p>
            </div>
            <div className="ml-auto">
              {settings?.live ? (
                <Badge className="bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 gap-1.5"
                  data-testid="evie-live-badge">
                  <Radio className="w-3.5 h-3.5" /> Live
                </Badge>
              ) : (
                <Badge className="bg-amber-500/15 text-amber-300 border border-amber-500/30 gap-1.5"
                  data-testid="evie-dormant-badge">
                  <AlertTriangle className="w-3.5 h-3.5" /> Dormant
                </Badge>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-5 py-6">
        {!settings?.live && (
          <div className="mb-5 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
            data-testid="evie-dormant-notice">
            The phone receptionist is <b>not active</b>. No calls are answered and nothing is billed.
            Add your Twilio &amp; OpenAI credentials in <b>Setup</b>, then toggle it on when you're ready to go live.
          </div>
        )}

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList data-testid="evie-tabs">
            <TabsTrigger value="calls" data-testid="tab-calls">Call Log</TabsTrigger>
            <TabsTrigger value="setup" data-testid="tab-setup">Setup</TabsTrigger>
          </TabsList>

          {/* ──────────────── CALL LOG ──────────────── */}
          <TabsContent value="calls" className="mt-5">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
              <Stat label="Calls Today" value={summary.today ?? 0} icon={Phone} tone="text-blue-600" />
              <Stat label="Total Calls" value={summary.total ?? 0} icon={PhoneCall} tone="text-slate-600" />
              <Stat label="Voicemails" value={summary.voicemails ?? 0} icon={Voicemail} tone="text-amber-600" />
              <Stat label="Open Follow-ups" value={summary.open_followups ?? 0} icon={AlertTriangle} tone="text-rose-600" />
            </div>

            <div className="flex flex-wrap items-end gap-3 mb-4">
              <div className="flex-1 min-w-[200px]">
                <Label className="text-xs text-slate-500">Search</Label>
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
                  <Input className="pl-8" placeholder="Number, name, summary…" value={filters.q}
                    onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                    onKeyDown={(e) => e.key === 'Enter' && loadCalls()}
                    data-testid="evie-search-input" />
                </div>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Status</Label>
                <Select value={filters.status} onValueChange={(v) => { const f = { ...filters, status: v }; setFilters(f); loadCalls(f); }}>
                  <SelectTrigger className="w-40" data-testid="evie-status-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="voicemail">Voicemail</SelectItem>
                    <SelectItem value="transferred">Transferred</SelectItem>
                    <SelectItem value="in_progress">In progress</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Follow-up</Label>
                <Select value={filters.follow_up} onValueChange={(v) => { const f = { ...filters, follow_up: v }; setFilters(f); loadCalls(f); }}>
                  <SelectTrigger className="w-36" data-testid="evie-followup-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="done">Resolved</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button variant="outline" onClick={() => loadCalls()} data-testid="evie-refresh-btn">
                <RefreshCw className="w-4 h-4" />
              </Button>
              <Button variant="outline" onClick={backfillSummaries} disabled={backfilling}
                data-testid="evie-backfill-btn" title="Generate AI summaries for older calls missing them">
                {backfilling ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                <span className="ml-1.5 hidden sm:inline">Backfill summaries</span>
              </Button>
            </div>

            {loading ? (
              <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
            ) : calls.length === 0 ? (
              <Card><CardContent className="py-16 text-center text-slate-500" data-testid="evie-no-calls">
                No calls yet. Once the receptionist is live, calls will appear here.
              </CardContent></Card>
            ) : (
              <div className="space-y-2" data-testid="evie-call-list">
                {calls.map((c) => (
                  <Card key={c.id} className="cursor-pointer hover:border-teal-300 transition-colors"
                    onClick={() => openDetail(c.id)} data-testid={`evie-call-row-${c.id}`}>
                    <CardContent className="py-3 px-4 flex items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-800">{c.client_name || c.from_number || 'Unknown caller'}</span>
                          {c.intent && <Badge variant="outline" className="text-xs">{INTENT_LABEL[c.intent] || c.intent}</Badge>}
                          {c.follow_up_needed && c.follow_up_status === 'open' && (
                            <Badge className="bg-rose-100 text-rose-700 text-xs">Follow-up</Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-500 truncate">{c.summary || (c.voicemail_transcription ? `Voicemail: ${c.voicemail_transcription}` : 'No summary yet')}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <Badge className={`${STATUS_STYLE[c.status] || 'bg-slate-100 text-slate-600'} text-xs`}>{c.status}</Badge>
                        <p className="text-xs text-slate-400 mt-1">{fmtDT(c.started_at)} · {fmtDur(c.duration_sec)}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* ──────────────── SETUP ──────────────── */}
          <TabsContent value="setup" className="mt-5">
            {!settings ? (
              <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
            ) : (
              <SetupForm settings={settings} saving={savingSettings} onSave={saveSettings} onCopy={copy} onRefresh={loadSettings} />
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* ──────────────── CALL DETAIL DIALOG ──────────────── */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="evie-call-detail">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  {detail.client_name || detail.from_number || 'Call'}
                  <Badge className={`${STATUS_STYLE[detail.status]} text-xs ml-1`}>{detail.status}</Badge>
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 text-sm">
                <div className="grid grid-cols-2 gap-3 text-slate-600">
                  <div><span className="text-slate-400">From</span><br />{detail.from_number || '—'}</div>
                  <div><span className="text-slate-400">When</span><br />{fmtDT(detail.started_at)}</div>
                  <div><span className="text-slate-400">Duration</span><br />{fmtDur(detail.duration_sec)}</div>
                  <div><span className="text-slate-400">Intent</span><br />{INTENT_LABEL[detail.intent] || detail.intent || '—'}</div>
                </div>

                {detail.summary && (
                  <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
                    <p className="text-xs font-semibold text-slate-500 mb-1">SUMMARY</p>
                    <p className="text-slate-700">{detail.summary}</p>
                  </div>
                )}

                {detail.booking_request && (
                  <div className="rounded-lg bg-teal-50 border border-teal-200 p-3" data-testid="evie-booking-request">
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <p className="text-xs font-semibold text-teal-700">BOOKING REQUEST</p>
                      {detail.booking_shift_id ? (
                        <button
                          onClick={() => navigate(`/admin/shifts/${detail.booking_shift_id}`)}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:underline"
                          data-testid="evie-view-shift">
                          <CheckCircle2 className="w-3.5 h-3.5" /> Shift created — View shift
                          <ExternalLink className="w-3 h-3" />
                        </button>
                      ) : (
                        <Button size="sm" className="h-7 px-2.5 text-xs bg-teal-600 hover:bg-teal-700"
                          onClick={createShiftFromCall} data-testid="evie-create-shift">
                          <CalendarPlus className="w-3.5 h-3.5 mr-1" /> Create shift from this call
                        </Button>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-slate-700">
                      {BOOKING_FIELDS.map(([k, label]) => {
                        const v = detail.booking_request[k];
                        if (v == null || v === '' || (Array.isArray(v) && v.length === 0)) return null;
                        return (
                          <div key={k} className="truncate">
                            <span className="text-slate-400">{label}: </span>
                            {fmtBookingVal(k, v)}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {(detail.voicemail_url || detail.voicemail_transcription) && (
                  <div className="rounded-lg bg-amber-50 border border-amber-200 p-3">
                    <p className="text-xs font-semibold text-amber-700 mb-1 flex items-center gap-1"><Voicemail className="w-3.5 h-3.5" /> VOICEMAIL</p>
                    {detail.voicemail_transcription && <p className="text-slate-700 mb-2">{detail.voicemail_transcription}</p>}
                    {detail.voicemail_url && <a href={detail.voicemail_url} target="_blank" rel="noreferrer" className="text-teal-600 underline text-xs">Listen to recording</a>}
                  </div>
                )}

                {Array.isArray(detail.transcript) && detail.transcript.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-slate-500 mb-2">TRANSCRIPT</p>
                    <div className="space-y-2 max-h-64 overflow-y-auto pr-1" data-testid="evie-transcript">
                      {detail.transcript.map((t, i) => (
                        <div key={i} className={`flex ${t.role === 'caller' ? 'justify-start' : 'justify-end'}`}>
                          <div className={`max-w-[80%] rounded-lg px-3 py-1.5 text-sm ${t.role === 'caller' ? 'bg-slate-100 text-slate-700' : 'bg-teal-600 text-white'}`}>
                            {t.text}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {detail.follow_up_needed && (
                  <div className="border-t pt-3">
                    <Label className="text-xs text-slate-500">Follow-up notes</Label>
                    <Textarea value={detailNotes} onChange={(e) => setDetailNotes(e.target.value)}
                      placeholder="What was done / next step…" className="mt-1" data-testid="evie-followup-notes" />
                    <div className="flex gap-2 mt-2">
                      {detail.follow_up_status === 'open' ? (
                        <Button size="sm" onClick={() => markFollowUp('done')} data-testid="evie-mark-done">
                          <CheckCircle2 className="w-4 h-4 mr-1" /> Mark resolved
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => markFollowUp('open')} data-testid="evie-reopen">Reopen</Button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value, icon: Icon, tone }) {
  return (
    <Card><CardContent className="py-3 px-4 flex items-center gap-3">
      <Icon className={`w-5 h-5 ${tone}`} />
      <div>
        <p className="text-xl font-semibold text-slate-800 leading-none">{value}</p>
        <p className="text-xs text-slate-500 mt-1">{label}</p>
      </div>
    </CardContent></Card>
  );
}

function SetupForm({ settings, saving, onSave, onCopy, onRefresh }) {
  const [form, setForm] = useState({
    twilio_account_sid: settings.twilio_account_sid || '',
    twilio_auth_token: '',
    twilio_phone_number: settings.twilio_phone_number || '',
    openai_api_key: '',
    openai_realtime_model: settings.openai_realtime_model || 'gpt-realtime',
    voice: settings.voice || 'shimmer',
    greeting: settings.greeting || '',
    public_base_url: settings.public_base_url || '',
    staffing_coordinator_number: settings.staffing_coordinator_number || '',
    notify_emails: settings.notify_emails || '',
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = () => {
    const patch = { ...form };
    if (!patch.twilio_auth_token) delete patch.twilio_auth_token;
    if (!patch.openai_api_key) delete patch.openai_api_key;
    onSave(patch);
  };

  return (
    <div className="space-y-5" data-testid="evie-setup-form">
      {/* Activation */}
      <Card><CardContent className="py-4 px-5 flex items-center justify-between">
        <div>
          <p className="font-medium text-slate-800">Receptionist status</p>
          <p className="text-sm text-slate-500">
            {settings.credentials_ready
              ? 'All credentials present. Toggle on to answer live calls.'
              : 'Add all credentials below and save before you can go live.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-sm ${settings.live ? 'text-emerald-600' : 'text-slate-400'}`}>{settings.live ? 'Live' : 'Off'}</span>
          <Switch checked={!!settings.enabled} disabled={!settings.credentials_ready && !settings.enabled}
            onCheckedChange={(v) => onSave({ enabled: v })} data-testid="evie-enable-toggle" />
        </div>
      </CardContent></Card>

      {/* Credentials */}
      <Card><CardContent className="py-4 px-5 space-y-4">
        <p className="font-medium text-slate-800">Credentials (stored securely server-side)</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Twilio Account SID">
            <Input value={form.twilio_account_sid} onChange={(e) => set('twilio_account_sid', e.target.value)}
              placeholder="ACxxxxxxxx" data-testid="evie-twilio-sid" />
          </Field>
          <Field label="Twilio Auth Token" hint={settings.twilio_auth_token_set ? `Saved (${settings.twilio_auth_token_masked})` : 'Not set'}>
            <Input type="password" value={form.twilio_auth_token} onChange={(e) => set('twilio_auth_token', e.target.value)}
              placeholder={settings.twilio_auth_token_set ? 'Leave blank to keep' : 'Enter token'} data-testid="evie-twilio-token" />
          </Field>
          <Field label="Twilio Phone Number" hint="E.164 e.g. +14705551234">
            <Input value={form.twilio_phone_number} onChange={(e) => set('twilio_phone_number', e.target.value)}
              placeholder="+1470…" data-testid="evie-twilio-number" />
          </Field>
          <Field label="OpenAI API Key" hint={settings.openai_api_key_set ? `Saved (${settings.openai_api_key_masked})` : 'Not set — required for live voice'}>
            <Input type="password" value={form.openai_api_key} onChange={(e) => set('openai_api_key', e.target.value)}
              placeholder={settings.openai_api_key_set ? 'Leave blank to keep' : 'sk-…'} data-testid="evie-openai-key" />
          </Field>
          <Field label="Realtime Model">
            <Input value={form.openai_realtime_model} onChange={(e) => set('openai_realtime_model', e.target.value)}
              placeholder="gpt-realtime" data-testid="evie-model" />
          </Field>
          <Field label="Evie's Voice">
            <Select value={form.voice} onValueChange={(v) => set('voice', v)}>
              <SelectTrigger data-testid="evie-voice"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(settings.voice_options || ['shimmer']).map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
        </div>
      </CardContent></Card>

      {/* Operational */}
      <Card><CardContent className="py-4 px-5 space-y-4">
        <p className="font-medium text-slate-800">Routing &amp; notifications</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Staffing Coordinator #" hint="Live transfer for URGENT staffing only (never your public line)">
            <Input value={form.staffing_coordinator_number} onChange={(e) => set('staffing_coordinator_number', e.target.value)}
              placeholder="+14044820018" data-testid="evie-coordinator" />
          </Field>
          <Field label="Notify Emails" hint="Comma-separated — booking requests, inquiries & voicemails">
            <Input value={form.notify_emails} onChange={(e) => set('notify_emails', e.target.value)}
              placeholder="ops@ievntur.com, bookings@ievntur.com" data-testid="evie-notify-emails" />
          </Field>
          <Field label="Public Base URL" hint="Your deployed domain (used for Twilio webhooks)">
            <Input value={form.public_base_url} onChange={(e) => set('public_base_url', e.target.value)}
              placeholder="https://app.ievntur.com" data-testid="evie-base-url" />
          </Field>
        </div>
        <Field label="Greeting (guidance for Evie)">
          <Textarea value={form.greeting} onChange={(e) => set('greeting', e.target.value)} data-testid="evie-greeting" />
        </Field>
      </CardContent></Card>

      <div className="flex gap-2">
        <Button onClick={submit} disabled={saving} data-testid="evie-save-settings">
          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save settings
        </Button>
        <Button variant="outline" onClick={onRefresh}><RefreshCw className="w-4 h-4" /></Button>
      </div>

      {/* Connection guide */}
      <Card><CardContent className="py-4 px-5 space-y-3">
        <p className="font-medium text-slate-800 flex items-center gap-2"><PhoneForwarded className="w-4 h-4 text-teal-600" /> Go-live checklist (no number porting)</p>
        <ol className="text-sm text-slate-600 space-y-2 list-decimal pl-5">
          <li>In Twilio Console, set your Twilio number's <b>Voice → A call comes in</b> webhook (POST) to:
            <CopyRow text={settings.webhook_url} onCopy={onCopy} />
          </li>
          <li>Set the <b>Call status changes</b> callback (POST) to:
            <CopyRow text={settings.status_callback_url} onCopy={onCopy} />
          </li>
          <li>In <b>Google Voice → Settings → Calls → Call forwarding</b>, add your Twilio number and verify it. Your Google Voice business number stays yours — calls simply forward to Twilio.</li>
          <li>Save all credentials above, then flip the status toggle to <b>Live</b>. Place a test call.</li>
        </ol>
        <p className="text-xs text-slate-400">Note: Google's docs list forwarding to an automated system as best-effort. If Workspace blocks it, publish the Twilio number directly — your Google Voice number is unaffected either way.</p>
      </CardContent></Card>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <Label className="text-xs text-slate-500">{label}</Label>
      <div className="mt-1">{children}</div>
      {hint && <p className="text-[11px] text-slate-400 mt-1">{hint}</p>}
    </div>
  );
}

function CopyRow({ text, onCopy }) {
  return (
    <div className="flex items-center gap-2 mt-1 bg-slate-100 rounded px-2 py-1 font-mono text-xs text-slate-700">
      <span className="truncate flex-1">{text}</span>
      <button onClick={() => onCopy(text)} className="text-slate-500 hover:text-slate-800"><Copy className="w-3.5 h-3.5" /></button>
    </div>
  );
}
